import React, { useState } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import Badge from "@react/shared/ui/Badge"

import MilestoneRow       from "./MilestoneRow"
import AcceptanceBanner   from "./AcceptanceBanner"
import ActionButton       from "./ActionButton"
import InvoicesSection    from "./InvoicesSection"
import DownPaymentModal   from "./DownPaymentModal"
import AcceptanceModal    from "./AcceptanceModal"
import DgdModal           from "./DgdModal"
import BankGuaranteeModal from "./BankGuaranteeModal"
import { releaseRetention } from "../api/retention"
import { fmt, dateFmt } from "../helpers/format"

const INVOICE_TONE = { "In progress": "warning", "Confirmed": "blue", "Paid": "success" }

const SituationsTable = ({ items }) => (
  <table className="min-w-full text-[12.5px]">
    <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
      <tr>
        <th className="text-left font-semibold px-4 py-2">{I18n.t("projects.milestones.table.situation")}</th>
        <th className="text-left font-semibold px-4 py-2">{I18n.t("projects.milestones.table.date")}</th>
        <th className="text-left font-semibold px-4 py-2">{I18n.t("projects.status")}</th>
        <th className="text-right font-semibold px-4 py-2">{I18n.t("projects.milestones.table.amount")}</th>
        <th className="text-right font-semibold px-4 py-2">{I18n.t("projects.milestones.table.paid")}</th>
      </tr>
    </thead>
    <tbody className="divide-y divide-slate-100">
      {items.map(situation => (
        <tr key={situation.id} className="hover:bg-slate-50/70">
          <td className="px-4 py-2">
            <a href={situation.path} className="font-medium text-slate-900 hover:text-blue-700">
              {situation.number}
            </a>
          </td>
          <td className="px-4 py-2 text-slate-600 tabular-nums">{dateFmt(situation.date)}</td>
          <td className="px-4 py-2">
            {situation.status_label && (
              <Badge tone={INVOICE_TONE[situation.status] || "neutral"} variant="soft" size="sm">
                {situation.status_label}
              </Badge>
            )}
          </td>
          <td className="px-4 py-2 text-right tabular-nums font-semibold text-slate-900">{fmt(situation.total)}</td>
          <td className="px-4 py-2 text-right tabular-nums text-slate-600">{fmt(situation.paid)}</td>
        </tr>
      ))}
    </tbody>
  </table>
)

const MilestonesSection = ({
  standard,
  standardInvoicing,
  bankGuarantee,
  downPayment,
  situations,
  acceptance,
  retention,
  guarantee,
  dgd,
  project,
  policies,
}) => {
  const [openModal, setOpenModal]   = useState(null)
  const [acceptanceData, setAcceptance] = useState(acceptance)
  const [, setRetention]                = useState(retention)
  const [gpa, setGpa]                   = useState(guarantee || {})

  if (standard) {
    return (
      <section id="section-milestones" className="scroll-mt-24">
        <InvoicesSection standardInvoicing={standardInvoicing} />
        <AcceptanceBanner acceptance={acceptance} policies={policies} />
      </section>
    )
  }

  const projectId    = project?.id
  const dp           = downPayment || {}
  const sits         = situations || { items: [], count: 0 }
  const acc          = acceptanceData || {}
  const caution      = bankGuarantee || {}
  const closure      = dgd || {}

  const close = () => setOpenModal(null)

  const handleRelease = () => {
    const warning = gpa.blocking_reserves > 0
      ? `${I18n.t("projects.milestones.guarantee.release_warning", { count: gpa.blocking_reserves })}\n\n`
      : ""
    if (!window.confirm(warning + I18n.t("projects.retention.confirm_release", { amount: gpa.retention?.cumul?.formatted }))) return

    releaseRetention(projectId).then(response => {
      if (response?.errors) return flashAlert(response.errors.join(", "))
      setRetention(response)
      setGpa(g => ({ ...g, status: "released", releasable: false,
                     retention: { ...(g.retention || {}), released: true, released_at: response.released_at } }))
      flashNotice(I18n.t("projects.retention.released_success"))
    })
  }

  return (
    <section id="section-milestones" className="px-7 scroll-mt-24">
      <header className="pl-[16px] mb-3">
        <h2 className="text-[15px] font-semibold text-slate-900">{I18n.t("projects.milestones.title")}</h2>
        <p className="text-[12.5px] text-slate-500 mt-0.5">
          {I18n.t("projects.milestones.subtitle")}
        </p>
      </header>

      <div className="flex flex-col gap-3">
        {/* Au démarrage: Acompte */}
        <MilestoneRow
          step="1"
          icon="hand-holding-dollar"
          tone={dp.exists ? "done" : "todo"}
          title={I18n.t("projects.milestones.down_payment.title")}
          hint={dp.exists ? null : I18n.t("projects.milestones.down_payment.hint", { pct: dp.default_pct || 30 })}
          status={dp.exists && dp.invoice?.status_label
            ? { tone: INVOICE_TONE[dp.invoice.status] || "neutral", label: dp.invoice.status_label }
            : null}
          figures={dp.exists ? [
            { label: I18n.t("projects.milestones.down_payment.amount"),    value: fmt(dp.invoice?.subtotal) },
            { label: I18n.t("projects.milestones.down_payment.deducted"),  value: fmt(dp.deducted),  muted: true },
            { label: I18n.t("projects.milestones.down_payment.remaining"), value: fmt(dp.remaining) },
          ] : []}
          action={
            dp.exists
              ? <a href={dp.invoice?.path} className="text-[12.5px] font-medium text-blue-700 hover:underline">
                  {dp.invoice?.number || I18n.t("projects.milestones.view_invoice")}
                </a>
              : policies?.can_create_down_payment
                ? <ActionButton onClick={() => setOpenModal("down_payment")}>{I18n.t("projects.milestones.down_payment.action")}</ActionButton>
                : null
          }
        />

        {/* Pendant les travaux: Situations */}
        <MilestoneRow
          step="2"
          icon="file-invoice"
          tone={sits.count > 0 ? "done" : "todo"}
          title={I18n.t("projects.milestones.situations.title")}
          hint={sits.count > 0 ? null : I18n.t("projects.milestones.situations.empty")}
          figures={sits.count > 0 ? [
            { label: I18n.t("projects.milestones.situations.issued"), value: String(sits.count), muted: true },
            { label: I18n.t("projects.milestones.situations.total"),  value: fmt(sits.total) },
          ] : []}
          action={sits.create_url ? <ActionButton href={sits.create_url}>{I18n.t("projects.milestones.situations.action")}</ActionButton> : null}
        >
          {sits.count > 0 && <SituationsTable items={sits.items} />}
        </MilestoneRow>

        {/* Le régime de garantie : la caution bancaire, alternative à la retenue.
            Placée après les situations : c'est quand la retenue commence à peser
            qu'on pense à la remplacer. */}
        <MilestoneRow
          step="3"
          icon="shield-halved"
          tone={caution.enabled ? "done" : "todo"}
          title={I18n.t("projects.milestones.bank_guarantee.title")}
          hint={caution.enabled ? null : I18n.t("projects.milestones.bank_guarantee.hint")}
          figures={caution.enabled ? [
            { label: I18n.t("projects.milestones.bank_guarantee.bank"),      value: caution.bank_name || "—", muted: true },
            { label: I18n.t("projects.milestones.bank_guarantee.reference"), value: caution.reference || "—", muted: true },
            { label: I18n.t("projects.milestones.bank_guarantee.amount"),    value: fmt(caution.amount) },
            caution.expires_on && { label: I18n.t("projects.milestones.bank_guarantee.expires_on"), value: dateFmt(caution.expires_on), muted: true },
          ] : []}
          action={policies?.can_edit_project
            ? <ActionButton icon={caution.enabled ? "pen" : "plus"} onClick={() => setOpenModal("bank_guarantee")}>
                {caution.enabled ? I18n.t("projects.milestones.bank_guarantee.edit") : I18n.t("projects.milestones.bank_guarantee.title")}
              </ActionButton>
            : null}
        />

        {/* Fin des travaux, départ de la GPA: Réception */}
        <MilestoneRow
          step="4"
          icon="stamp"
          tone={acc.exists ? (acc.acceptance?.finalized ? "done" : "watch") : "todo"}
          title={I18n.t("projects.milestones.acceptance.title")}
          hint={acc.exists ? null : I18n.t("projects.milestones.acceptance.hint")}
          status={acc.exists
            ? { tone: acc.acceptance?.finalized ? "success" : "warning",
                label: acc.acceptance?.finalized ? acc.acceptance?.decision_label : I18n.t("projects.executive_synthesis.draft", { defaultValue: "Brouillon" }) }
            : null}
          figures={acc.exists ? [
            { label: I18n.t("projects.milestones.acceptance.pronounced_on"), value: dateFmt(acc.acceptance?.acceptance_date) },
            { label: I18n.t("projects.milestones.acceptance.reserves"),      value: String(acc.acceptance?.reserves_snapshot_count ?? 0), muted: true },
          ] : []}
          action={policies?.can_manage_acceptance
            ? <ActionButton icon={acc.exists ? "pen" : "plus"} onClick={() => setOpenModal("acceptance")}>
                {acc.exists ? I18n.t("projects.milestones.acceptance.edit") : I18n.t("projects.milestones.acceptance.action")}
              </ActionButton>
            : null}
        />

        {/* Solde du marché: DGD */}
        <MilestoneRow
          step="5"
          icon="flag-checkered"
          tone={closure.exists ? "done" : "todo"}
          title={I18n.t("projects.milestones.dgd.title")}
          hint={closure.exists ? null : I18n.t("projects.milestones.dgd.hint")}
          status={closure.exists && closure.invoice?.status_label
            ? { tone: INVOICE_TONE[closure.invoice.status] || "neutral", label: closure.invoice.status_label }
            : null}
          figures={closure.exists ? [
            { label: I18n.t("projects.milestones.dgd.amount_excl_tax"), value: fmt(closure.invoice?.subtotal) },
            { label: I18n.t("projects.milestones.dgd.total_incl_tax"),  value: fmt(closure.invoice?.total) },
            { label: I18n.t("projects.milestones.dgd.issued_on"),       value: dateFmt(closure.invoice?.creation_date), muted: true },
          ] : []}
          action={
            closure.exists
              ? <a href={closure.invoice?.path} className="text-[12.5px] font-medium text-blue-700 hover:underline">
                  {closure.invoice?.number || I18n.t("projects.milestones.view_invoice")}
                </a>
              : policies?.can_create_dgd
                ? <ActionButton onClick={() => setOpenModal("dgd")}>{I18n.t("projects.milestones.dgd.action")}</ActionButton>
                : null
          }
        />
{/* Fin de GPA, un an après la réception: la garantie, nommée, et la
            retenue qu'elle commande */}
        <MilestoneRow
          step="6"
          icon={gpa.status === "released" ? "unlock" : "shield-halved"}
          tone={gpa.status === "released" ? "done"
              : gpa.status === "expired" || gpa.status === "ending_soon" ? "watch"
              : "todo"}
          title={I18n.t("projects.guarantee.title")}
          hint={
            gpa.status === "not_started"
              ? I18n.t("projects.milestones.guarantee.hint_not_started")
              : gpa.status === "released"
                ? null
                : I18n.t("projects.milestones.guarantee.hint_period", { start: dateFmt(gpa.starts_on), end: dateFmt(gpa.ends_on) })
          }
          status={{
            tone: gpa.status === "released" ? "success"
                : gpa.status === "expired" ? "danger"
                : gpa.status === "ending_soon" ? "warning"
                : "neutral",
            label: gpa.status === "ending_soon" ? `${I18n.t("projects.guarantee.statuses.ending_soon")} · ${gpa.days_left} ${I18n.t("projects.units.day_short")}`
                 : gpa.status === "expired"     ? `${I18n.t("projects.guarantee.statuses.expired")} · ${Math.abs(gpa.days_left)} ${I18n.t("projects.units.day_short")}`
                 : gpa.status_label,
          }}
          figures={[
            { label: I18n.t("projects.milestones.guarantee.retention"), value: fmt(gpa.retention?.cumul) },
            { label: I18n.t("projects.milestones.guarantee.rate"), value: `${Math.round((gpa.retention?.percentage ?? 5) * 100) / 100} %`, muted: true },
            !gpa.retention?.released && (gpa.retention?.cumul?.cents || 0) > 0 && {
              label: I18n.t("projects.milestones.guarantee.releasable_on"),
              value: gpa.ends_on ? dateFmt(gpa.ends_on) : I18n.t("projects.milestones.guarantee.releasable_default"),
              muted: true,
            },
            gpa.reserves?.reception > 0 && { label: I18n.t("projects.guarantee.reception_reserves"), value: String(gpa.reserves.reception), muted: true },
            gpa.reserves?.guarantee > 0 && { label: I18n.t("projects.guarantee.guarantee_reserves"), value: String(gpa.reserves.guarantee), muted: true },
            gpa.retention?.released && { label: I18n.t("projects.milestones.guarantee.released_on"), value: dateFmt(gpa.retention.released_at), muted: true },
          ]}
          action={gpa.releasable && policies?.can_release_retention
            ? <ActionButton icon="unlock" onClick={handleRelease}>{I18n.t("projects.milestones.guarantee.release")}</ActionButton>
            : null}
        >
          {/* The retention exists to secure exactly these. Releasing it while
              they are open gives the pledge back before the work is done. */}
          {gpa.blocking_reserves > 0 && !gpa.retention?.released && (
            <div className="px-4 py-2.5 bg-amber-50/70 text-[12px] text-amber-800 flex items-center gap-2">
              <FontAwesomeIcon icon="triangle-exclamation" className="text-[11px]" />
              {I18n.t("projects.milestones.guarantee.blocking_reserves", { count: gpa.blocking_reserves })}
            </div>
          )}
        </MilestoneRow>

      </div>

      <DownPaymentModal
        isOpen      = {openModal === "down_payment"}
        projectId   = {projectId}
        downPayment = {dp}
        onClose     = {close}
      />
      <AcceptanceModal
        isOpen            = {openModal === "acceptance"}
        projectId         = {projectId}
        acceptanceData    = {acc}
        liveReservesCount = {acc.live_reserves_count}
        canManage         = {!!policies?.can_manage_acceptance}
        onClose           = {close}
        onSaved           = {next => setAcceptance(d => ({ ...d, exists: next != null, acceptance: next }))}
      />
      <BankGuaranteeModal
        isOpen        = {openModal === "bank_guarantee"}
        projectId     = {projectId}
        bankGuarantee = {caution}
        onClose       = {close}
      />
      <DgdModal
        isOpen    = {openModal === "dgd"}
        projectId = {projectId}
        preview   = {closure.preview}
        onClose   = {close}
      />
    </section>
  )
}

export default MilestonesSection
